import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { StyledAddArt } from "./AddArt.styled";
import { BirdIcon, ImageIcon, PlusIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { TrashSimpleIcon } from "@phosphor-icons/react/dist/ssr";
import { useArt } from "../../stores/useArt";
import { useProfile } from "../../stores/useProfile";
import { ArtDto } from "../../Models/ArtDto";

export const AddArt = () => {
  const { addArt } = useArt();
  const { user } = useProfile();

  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedType, setSelectedType] = useState<"PAINTING" | "SCULPTURE">(
    "PAINTING"
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sellState, setSellState] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const filePreviews = newFiles.map((file) => URL.createObjectURL(file));

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setPreviewImages((prev) => [...prev, ...filePreviews]);
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setPreviewImages((prev) => {
      URL.revokeObjectURL(prev[indexToRemove]);
      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: "PAINTING" | "SCULPTURE" | null
  ) => {
    if (newValue !== null) {
      setSelectedType(newValue);
    }
  };

  const onSave = async () => {
    if (!user) return;

    const art: ArtDto = {
      title,
      description,
      sellState,
      type: selectedType,
      mainImage: selectedFiles[selectedIndex],
      files: selectedFiles,
      viewCount: 0,
      creationDate: new Date(),
    };

    const result = await addArt(user, art);
    if (result === 201) {
      console.log("✅ Art created successfully");

      setPreviewImages([]);
      setSelectedFiles([]);
      setSelectedIndex(0);
      setSelectedType("PAINTING");
      setTitle("");
      setDescription("");
      setSellState("")
    } else {
      console.error("❌ Failed to create art:", result);
    }
  };

  useEffect(() => {
    return () => {
      previewImages.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewImages]);

  return (
    <StyledAddArt>
      <Box className="imageWrapper">
        <Box className=""></Box>
        <Button variant="contained" component="label" className="addimageBtn">
          <PlusIcon size={64} />
          <input
            hidden
            accept="image/*"
            type="file"
            multiple
            onChange={handleFileChange}
          />
        </Button>
        {previewImages.map((src, index) => (
          <Box
            className={`image ${selectedIndex === index && "selected"}`}
            onClick={() => setSelectedIndex(index)}>
            <img
              key={index}
              src={src}
              alt={`preview-${index}`}
              className="previewImage"
            />
            <TrashSimpleIcon
              size={32}
              weight="bold"
              onClick={() => handleRemoveImage(index)}
            />
          </Box>
        ))}
      </Box>
      <Box className="artForm">
        <Box className="row">
          <Box className="column">
            <Typography variant="h3">Titel</Typography>
            <TextField
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Box>
          <Box className="column">
            <Typography variant="h3">Beschrijving</Typography>
            <TextField
              value={description}
              fullWidth
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
        </Box>
        <Box className="row">
          <Box className="column">
            <FormControl fullWidth>
              <Select
                labelId="type-label"
                id="type-select"
                label="Art Type"
                value={sellState}
                onChange={(e) => setSellState(e.target.value as string)}
              >
                <MenuItem value="Te Koop">Te Koop</MenuItem>
                <MenuItem value="Verkocht">Verkocht</MenuItem>
                <MenuItem value="Verkocht in opdracht">
                  Verkocht in opdracht
                </MenuItem>
                <MenuItem value="Verkocht / Op bestelling verkrijgbaar">
                  Verkocht / Op bestelling verkrijgbaar
                </MenuItem>
                <MenuItem value="Te koop in Atelier">
                  Te koop in Atelier
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={handleChange}
            className="pageButtons">
            <ToggleButton value="PAINTING" className="pageButton">
              <ImageIcon size={32} weight="bold" />
              <Typography variant="h3">Schilderij</Typography>
            </ToggleButton>
            <ToggleButton value="SCULPTURE" className="pageButton">
              <BirdIcon size={32} weight="bold" />
              <Typography variant="h3">Sculptuur</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box className="row">
          <Button className="savebtn" onClick={onSave}>
            Opslaan
          </Button>
        </Box>
      </Box>
    </StyledAddArt>
  );
};
